// src/components/landing/Footer.tsx
'use client'

import Link from 'next/link'
import { Instagram, Mail, Youtube } from 'lucide-react'
import Logo from '@/components/Logo'

type Column = {
  heading: string
  links: { label: string; href: string; external?: boolean; icon?: React.ReactNode }[]
}

const COLUMNS: Column[] = [
  {
    heading: 'Product',
    links: [
      { label: 'Tests', href: '/tests' },
      { label: 'Practice', href: '/practice' },
      { label: 'Learn', href: '/learn' },
      { label: 'Videos', href: '/videos' },
      { label: 'Score guarantee', href: '/guarantee' },
      { label: 'Quality pledge', href: '/quality' },
      { label: 'Student results', href: '/proof' },
      { label: 'Blog', href: '/blog' },
      { label: 'Pricing', href: '/pricing' },
    ],
  },
  {
    heading: 'Company',
    links: [
      { label: 'About', href: '/about' },
      { label: 'Privacy', href: '/privacy' },
      { label: 'Terms', href: '/terms' },
      // A school's procurement review looks for the accessibility statement in
      // the footer next to the other policy pages, so it lives here rather than
      // only being reachable from /privacy (which is where /security hides).
      { label: 'Accessibility', href: '/accessibility' },
    ],
  },
  {
    // Contact used to appear here AND under Company. One entry, in the column
    // someone actually scans when they need a person.
    heading: 'Support',
    links: [
      { label: 'Help', href: '/help' },
      { label: 'Contact', href: '/contact' },
    ],
  },
]

export default function Footer() {
  return (
    <footer className="border-t border-border bg-card text-muted-foreground">
      <div className="mx-auto max-w-7xl px-5 py-12 md:px-10">
        {/* Brand lockup above the link columns. md-size to give the
            footer a clear visual anchor without overpowering the
            content below. The link wraps the whole lockup so the
            brand row is also a "back to home" affordance. */}
        <div className="mb-10">
          <Link
            href="/"
            aria-label="UnlimitedTests home"
            className="inline-flex transition-opacity hover:opacity-80"
          >
            <Logo size="md" />
          </Link>
        </div>

        <div className="grid gap-10 md:grid-cols-4">
          {COLUMNS.map((col) => (
            <div key={col.heading}>
              <h3 className="text-sm font-semibold uppercase tracking-wider text-foreground">
                {col.heading}
              </h3>
              <ul className="mt-4 space-y-2.5">
                {col.links.map((link) => (
                  <li key={link.label}>
                    <Link
                      href={link.href}
                      className="text-sm text-muted-foreground transition hover:text-brand-ink"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}

          {/* Connect column with icons */}
          <div>
            <h3 className="text-sm font-semibold uppercase tracking-wider text-foreground">
              Connect
            </h3>
            <ul className="mt-4 flex items-center gap-3">
              <li>
                <a
                  href="https://www.instagram.com/unlimitedtests"
                  aria-label="UnlimitedTests on Instagram"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-border bg-background text-muted-foreground transition hover:border-brand/40 hover:text-brand-ink"
                >
                  <Instagram className="h-4 w-4" aria-hidden />
                </a>
              </li>
              <li>
                <a
                  href="https://www.youtube.com/@UnlimitedTests/"
                  aria-label="UnlimitedTests on YouTube"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-border bg-background text-muted-foreground transition hover:border-brand/40 hover:text-brand-ink"
                >
                  <Youtube className="h-4 w-4" aria-hidden />
                </a>
              </li>
              <li>
                <a
                  href="mailto:support@unlimitedtests.com"
                  aria-label="Email UnlimitedTests"
                  className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-border bg-background text-muted-foreground transition hover:border-brand/40 hover:text-brand-ink"
                >
                  <Mail className="h-4 w-4" aria-hidden />
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-10 border-t border-border pt-6 text-center text-xs text-muted-foreground">
          © 2026 UnlimitedTests. All rights reserved.
        </div>
      </div>
    </footer>
  )
}
